import { useAtomValue } from "jotai";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { StarStrikethrought } from "~/components/icons/star-strikethrought";
import { PaginationList } from "~/components/pagination/pagination-list";
import { OrderReview } from "~/components/statistics-rating/order-review";
import { SkeletonOrderReview } from "~/components/statistics-rating/skeleton-order-review";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ArrowDownIcon } from "~/components/ui/icons/arrow-down";
import { StarIcon } from "~/components/ui/icons/star";
import type { StatsQuery } from "~/graphql/graphql.gen";
import { useRatingsQuery } from "~/graphql/graphql.gen";
import { activeSaleState } from "~/stores/sales";
import { RatingsStatsSummary } from "~/views/analytics/ratings-stats-summary";

type RatingsProps = {
  stats?: StatsQuery["statsV2"];
};

export const Ratings: React.FC<RatingsProps> = ({ stats }) => {
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeSale = useAtomValue(activeSaleState);

  const typeOptions = [
    {
      key: "all",
      value: t("rating.all_review"),
    },
    {
      key: "with_comment",
      value: t("rating.review_comments_only"),
    },
  ];

  const ratingOptions = [
    { key: "-1", value: t("rating.all_star") },
    {
      key: "5",
      value: t("rating.5_star_only"),
      disabled:
        stats?.rateCountAggByRates.findIndex((c) => c.rate === 4) === -1,
    },
    {
      key: "4",
      value: t("rating.4_star_only"),
      disabled:
        stats?.rateCountAggByRates.findIndex((c) => c.rate === 3) === -1,
    },
    {
      key: "3",
      disabled:
        stats?.rateCountAggByRates.findIndex((c) => c.rate === 2) === -1,
      value: t("rating.3_star_only"),
    },
    {
      key: "2",
      disabled:
        stats?.rateCountAggByRates.findIndex((c) => c.rate === 1) === -1,
      value: t("rating.2_star_only"),
    },
    {
      key: "1",
      disabled:
        stats?.rateCountAggByRates.findIndex((c) => c.rate === 0) === -1,
      value: t("rating.1_star_only"),
    },
  ];

  const [offsetRate, setOffsetRate] = useState(
    searchParams.has("rate_offset")
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        parseInt(searchParams.get("rate_offset")!)
      : 0,
  );
  const [filterRate, setFilterRate] = useState(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    searchParams.has("rate") ? parseInt(searchParams.get("rate")!) : -1,
  );
  const [filterRateMessage, setFilterRateMessage] = useState(
    searchParams.has("rate_message")
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        searchParams.get("rate_message")!
      : "all",
  );

  const { data: ratingsData, isLoading: loadingRatings } = useRatingsQuery(
    {
      saleId: activeSale?.id ?? "",
      offset: offsetRate,
      onlyWithMessage: filterRateMessage !== "all",
      filter: filterRate > -1 ? filterRate - 1 : undefined,
    },
    {
      enabled: !!activeSale?.id,
      refetchOnWindowFocus: false,
    },
  );

  const onChangePage = (newPage: number) => {
    const newOffset = newPage - 1;
    setSearchParams((prev) => {
      prev.set("rate_offset", newOffset.toString());
      return prev;
    });
    setOffsetRate(newOffset);
    const element = document.querySelector("[data-slot='ratings']");
    element?.scrollIntoView({
      behavior: "auto",
      block: "start",
      inline: "nearest",
    });
  };

  const onChangeRating = (value: string | number) => {
    setSearchParams((prev) => {
      prev.set("rate_offset", "0");
      prev.set("rate", value.toString());
      return prev;
    });
    setOffsetRate(0);
    setFilterRate(+value);
  };

  const onChangeFilter = (value: string) => {
    setSearchParams((prev) => {
      prev.set("rate_offset", "0");
      prev.set("rate_message", value.toString());
      return prev;
    });
    setFilterRateMessage(value);
    setOffsetRate(0);
  };

  if (!stats?.rateCountAggByRates.length) {
    return (
      <div className="flex flex-row items-center gap-2 rounded-lg border border-[#ECECEC] p-6">
        <StarIcon className="size-6" />
        <p className="text-gray-700">{t("analytics.rate_text")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <RatingsStatsSummary
          rateAvg={stats.rateAvg ?? 0}
          rateCount={stats.rateCount}
          rateCountAggByRates={stats.rateCountAggByRates}
          onChangeRating={onChangeRating}
        />
      </div>
      <h5
        data-slot="ratings"
        className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-700"
      >
        {t("rating.filter_by")}
      </h5>
      <div className="flex flex-row space-x-4 border-b border-gray-300 pb-6 min-[961px]:space-x-9">
        <DropdownMenu>
          <DropdownMenuTrigger className="group flex shrink-0 items-center">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-green-900">
                {typeOptions.find((c) => c.key === filterRateMessage)?.value}
              </span>
              <ArrowDownIcon className="size-4 shrink-0 transition-all group-data-[state=open]:rotate-180" />
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            sideOffset={8}
            className="min-w-40 overflow-y-auto"
            style={{
              maxHeight: "var(--radix-dropdown-menu-content-available-height)",
            }}
          >
            {typeOptions.map((typeOption) => (
              <DropdownMenuItem
                key={typeOption.key}
                onSelect={() => onChangeFilter(typeOption.key)}
              >
                {typeOption.value}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger className="group flex shrink-0 items-center">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-semibold text-green-900">
                {
                  ratingOptions.find((c) => c.key === filterRate.toString())
                    ?.value
                }
              </span>
              <ArrowDownIcon className="size-4 shrink-0 transition-all group-data-[state=open]:rotate-180" />
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="start"
            sideOffset={8}
            className="min-w-40 overflow-y-auto"
            style={{
              maxHeight: "var(--radix-dropdown-menu-content-available-height)",
            }}
          >
            {ratingOptions.map((ratingOption) => (
              <DropdownMenuItem
                key={ratingOption.key}
                onSelect={() => onChangeRating(ratingOption.key)}
                disabled={ratingOption.disabled}
              >
                {ratingOption.value}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {loadingRatings ? (
        <div className="mb-6 divide-y divide-gray-300 border-b border-gray-300">
          <div className="py-6">
            <SkeletonOrderReview />
          </div>
          <div className="py-6">
            <SkeletonOrderReview />
          </div>
          <div className="py-6">
            <SkeletonOrderReview />
          </div>
          <div className="py-6">
            <SkeletonOrderReview />
          </div>
        </div>
      ) : ratingsData?.ratings?.totalCount ? (
        <div className="mb-6 divide-y divide-gray-300 border-b border-gray-300">
          {ratingsData.ratings.nodes.map((rate, i) => (
            <OrderReview key={rate.order_id + "_" + i} review={rate} />
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 py-6 text-gray-500">
          <StarStrikethrought />
          <p className="text-sm">{t("analytics.no_result")}</p>
        </div>
      )}

      <PaginationList
        currentPage={offsetRate + 1}
        totalCount={ratingsData?.ratings?.totalCount ?? 0}
        pageSize={16}
        onChange={onChangePage}
        className="mt-4"
      />
    </div>
  );
};
